import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export default function AttendanceTimesheet() {
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');

  // ── State ──
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [gridData, setGridData] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [error, setError] = useState('');

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [filterMode, setFilterMode] = useState('');
  const [departments, setDepartments] = useState([]);

  // Modals
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [showOvertimeModal, setShowOvertimeModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showLockModal, setShowLockModal] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);

  // Modal data
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [recordForm, setRecordForm] = useState({});
  const [overtimeList, setOvertimeList] = useState([]);
  const [overtimeForm, setOvertimeForm] = useState({});
  const [summaryData, setSummaryData] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [pendingActions, setPendingActions] = useState([]);
  const [showPendingModal, setShowPendingModal] = useState(false);

  // Bulk
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [bulkDates, setBulkDates] = useState('');
  const [bulkStatusId, setBulkStatusId] = useState('');
  const [bulkAction, setBulkAction] = useState('set_status');

  const [activeTab, setActiveTab] = useState('grid'); // grid | overtime | schedules | pending | audit

  // ── Fetch Grid ──
  const fetchGrid = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ month, year });
      if (searchTerm) params.append('search', searchTerm);
      if (filterDept) params.append('department_id', filterDept);
      if (filterMode) params.append('attendance_mode', filterMode);

      const res = await fetch(`${API}/companies/${companyId}/attendance/grid?${params}`, {
        headers: getAuthHeaders(),
      });
      if (res.status === 401 || res.status === 403) { navigate('/login'); return; }
      const json = await res.json();
      if (json.status === 'success') {
        setGridData(json.data);
      } else {
        setError(json.message || 'Failed to load attendance data.');
      }
    } catch (err) {
      setError('Network error loading attendance.');
    } finally {
      setLoading(false);
    }
  }, [companyId, month, year, searchTerm, filterDept, filterMode, navigate]);

  useEffect(() => { fetchGrid(); }, [fetchGrid]);

  // Fetch departments for filter
  useEffect(() => {
    if (!companyId) return;
    fetch(`${API}/companies/${companyId}/departments`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(json => { if (json.data) setDepartments(json.data); })
      .catch(() => {});
  }, [companyId]);

  // ── Helpers ──
  const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  const getStatusColor = (day) => {
    if (!day || !day.status) return '#f8f9fa';
    if (day.status_color) return day.status_color;
    const s = day.status.toLowerCase();
    if (s.includes('present') || s === 'on site') return '#d4edda';
    if (s === 'remote') return '#cce5ff';
    if (s.includes('leave')) return '#fff3cd';
    if (s.includes('absent') || s === 'missing') return '#f8d7da';
    if (s.includes('holiday')) return '#e2e3e5';
    return '#f8f9fa';
  };

  const getStatusLetter = (day) => {
    if (!day || !day.status) return '';
    if (day.status_code) return day.status_code;
    const s = day.status.toLowerCase();
    if (s.includes('present') || s === 'on site') return 'P';
    if (s === 'remote') return 'R';
    if (s.includes('leave')) return 'L';
    if (s.includes('absent') || s === 'missing') return 'A';
    if (s.includes('holiday')) return 'H';
    return day.status.charAt(0).toUpperCase();
  };

  // ── Record Modal ──
  const openRecordModal = (emp, dayNum) => {
    const existing = emp.days?.[dayNum]?.records?.[0];
    setSelectedEmployee(emp.employee);
    setSelectedDay(dayNum);
    setRecordForm({
      employee_id: emp.employee.id,
      date: `${year}-${String(month).padStart(2,'0')}-${String(dayNum).padStart(2,'0')}`,
      attendance_mode: existing?.attendance_mode || emp.employee.attendance_mode || 'daily',
      hr_attendance_status_id: existing?.hr_attendance_status_id || '',
      from_time: existing?.from_time || '',
      to_time: existing?.to_time || '',
      break_minutes: existing?.break_minutes || 0,
      notes: existing?.notes || '',
      transportation_eligible: existing?.transportation_eligible || false,
      existing_id: existing?.id || null,
    });
    setShowRecordModal(true);
  };

  const saveRecord = async () => {
    try {
      const url = recordForm.existing_id
        ? `${API}/companies/${companyId}/attendance/${recordForm.existing_id}`
        : `${API}/companies/${companyId}/attendance`;
      const method = recordForm.existing_id ? 'PUT' : 'POST';

      const body = { ...recordForm };
      delete body.existing_id;

      const res = await fetch(url, {
        method,
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (json.status === 'success') {
        setShowRecordModal(false);
        fetchGrid();
      } else {
        window.alert(json.message || 'Error saving record.');
      }
    } catch {
      window.alert('Network error saving record.');
    }
  };

  const deleteRecord = async () => {
    if (!recordForm.existing_id) return;
    if (!window.confirm('Delete this attendance record?')) return;
    try {
      const res = await fetch(`${API}/companies/${companyId}/attendance/${recordForm.existing_id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const json = await res.json();
      if (json.status === 'success') {
        setShowRecordModal(false);
        fetchGrid();
      } else {
        window.alert(json.message || 'Error deleting.');
      }
    } catch {
      window.alert('Network error.');
    }
  };

  // ── Overtime ──
  const fetchOvertime = async () => {
    try {
      const params = new URLSearchParams({ month, year });
      const res = await fetch(`${API}/companies/${companyId}/attendance/overtime?${params}`, {
        headers: getAuthHeaders(),
      });
      const json = await res.json();
      if (json.status === 'success') setOvertimeList(json.data || []);
    } catch { /* ignore */ }
  };

  const submitOvertime = async () => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/attendance/overtime`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(overtimeForm),
      });
      const json = await res.json();
      if (json.status === 'success') {
        setShowOvertimeModal(false);
        setOvertimeForm({});
        fetchOvertime();
      } else {
        window.alert(json.message || 'Error.');
      }
    } catch {
      window.alert('Network error.');
    }
  };

  const handleOvertimeAction = async (id, action) => {
    const reason = action === 'reject' ? window.prompt('Rejection reason:') : null;
    if (action === 'reject' && reason === null) return;
    try {
      const res = await fetch(`${API}/companies/${companyId}/attendance/overtime/${id}/${action}`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      const json = await res.json();
      if (json.status === 'success') fetchOvertime();
      else window.alert(json.message || 'Error.');
    } catch {
      window.alert('Network error.');
    }
  };

  // ── Bulk ──
  const submitBulk = async () => {
    const dates = bulkDates.split(',').map(d => d.trim()).filter(Boolean);
    if (!selectedEmployees.length || !dates.length) {
      window.alert('Select employees and enter dates.');
      return;
    }
    try {
      const res = await fetch(`${API}/companies/${companyId}/attendance/bulk`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: bulkAction,
          employee_ids: selectedEmployees,
          dates,
          hr_attendance_status_id: bulkStatusId || undefined,
        }),
      });
      const json = await res.json();
      if (json.status === 'success') {
        setShowBulkModal(false);
        setSelectedEmployees([]);
        setBulkDates('');
        fetchGrid();
        window.alert(`Done: ${json.data.created} created, ${json.data.updated} updated, ${json.data.skipped} skipped`);
      } else {
        window.alert(json.message || 'Error.');
      }
    } catch {
      window.alert('Network error.');
    }
  };

  // ── Lock/Unlock ──
  const handleLock = async (lockAction) => {
    const notes = window.prompt(`${lockAction === 'lock' ? 'Lock' : 'Unlock'} reason (optional):`);
    if (notes === null) return;
    try {
      const res = await fetch(`${API}/companies/${companyId}/attendance/${lockAction}`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ month, year, notes }),
      });
      const json = await res.json();
      if (json.status === 'success') {
        fetchGrid();
        window.alert(`Period ${lockAction}ed.`);
      } else {
        window.alert(json.message || 'Error.');
      }
    } catch {
      window.alert('Network error.');
    }
  };

  // ── Employee Summary ──
  const openSummary = async (empId) => {
    try {
      const params = new URLSearchParams({ month, year });
      const res = await fetch(`${API}/companies/${companyId}/attendance/summary/${empId}?${params}`, {
        headers: getAuthHeaders(),
      });
      const json = await res.json();
      if (json.status === 'success') {
        setSummaryData(json.data);
        setShowSummaryModal(true);
      }
    } catch {
      window.alert('Error fetching summary.');
    }
  };

  // ── Pending Actions ──
  const fetchPending = async () => {
    try {
      const params = new URLSearchParams({ month, year });
      const res = await fetch(`${API}/companies/${companyId}/attendance/pending?${params}`, {
        headers: getAuthHeaders(),
      });
      const json = await res.json();
      if (json.status === 'success') setPendingActions(json.data || []);
    } catch { /* ignore */ }
  };

  // ── Audit ──
  const fetchAudit = async () => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/attendance/audit`, {
        headers: getAuthHeaders(),
      });
      const json = await res.json();
      if (json.status === 'success') setAuditLogs(json.data || []);
    } catch { /* ignore */ }
  };

  // Tab switching
  useEffect(() => {
    if (activeTab === 'overtime') fetchOvertime();
    if (activeTab === 'pending') fetchPending();
    if (activeTab === 'audit') fetchAudit();
    // eslint-disable-next-line
  }, [activeTab, month, year]);

  if (!companyId) {
    return (
      <div style={styles.container}>
        <p style={{ color: '#dc3545' }}>No company selected. Please select a company from the sidebar.</p>
      </div>
    );
  }

  // ═════════════════════════════════════════
  //  RENDER
  // ═════════════════════════════════════════

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>📋 Attendance & Timesheet</h1>
        <div style={styles.headerActions}>
          {gridData?.is_locked ? (
            <button style={styles.btnWarning} onClick={() => handleLock('unlock')}>🔓 Unlock Period</button>
          ) : (
            <button style={styles.btnSecondary} onClick={() => handleLock('lock')}>🔒 Lock Period</button>
          )}
          <button style={styles.btnPrimary} onClick={() => setShowBulkModal(true)}>📦 Bulk Action</button>
        </div>
      </div>

      {/* Lock Banner */}
      {gridData?.is_locked && (
        <div style={styles.lockBanner}>🔒 This attendance period is locked. Records cannot be modified without unlocking.</div>
      )}

      {/* Month Navigation */}
      <div style={styles.monthNav}>
        <button style={styles.navBtn} onClick={() => {
          if (month === 1) { setMonth(12); setYear(y => y - 1); }
          else setMonth(m => m - 1);
        }}>◀</button>
        <span style={styles.monthLabel}>{monthNames[month - 1]} {year}</span>
        <button style={styles.navBtn} onClick={() => {
          if (month === 12) { setMonth(1); setYear(y => y + 1); }
          else setMonth(m => m + 1);
        }}>▶</button>
      </div>

      {/* Summary Cards */}
      {gridData?.summary && (
        <div style={styles.summaryRow}>
          <div style={styles.summaryCard}>
            <div style={styles.summaryNum}>{gridData.summary.total_employees}</div>
            <div style={styles.summaryLabel}>Employees</div>
          </div>
          <div style={{ ...styles.summaryCard, borderLeft: '4px solid #dc3545' }}>
            <div style={styles.summaryNum}>{gridData.summary.missing_attendance}</div>
            <div style={styles.summaryLabel}>Missing</div>
          </div>
          <div style={{ ...styles.summaryCard, borderLeft: '4px solid #fd7e14' }}>
            <div style={styles.summaryNum}>{gridData.summary.late_records}</div>
            <div style={styles.summaryLabel}>Late</div>
          </div>
          <div style={{ ...styles.summaryCard, borderLeft: '4px solid #17a2b8' }}>
            <div style={styles.summaryNum}>{gridData.summary.total_overtime?.toFixed(1) || 0}h</div>
            <div style={styles.summaryLabel}>Overtime</div>
          </div>
          <div style={{ ...styles.summaryCard, borderLeft: '4px solid #ffc107' }}>
            <div style={styles.summaryNum}>{gridData.summary.pending_overtime}</div>
            <div style={styles.summaryLabel}>Pending OT</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={styles.filterRow}>
        <input
          type="text" placeholder="Search by name or code..."
          value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
          style={styles.searchInput}
        />
        <select value={filterDept} onChange={e => setFilterDept(e.target.value)} style={styles.filterSelect}>
          <option value="">All Departments</option>
          {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <select value={filterMode} onChange={e => setFilterMode(e.target.value)} style={styles.filterSelect}>
          <option value="">All Modes</option>
          <option value="daily">Daily</option>
          <option value="hourly">Hourly</option>
        </select>
      </div>

      {/* Tabs */}
      <div style={styles.tabRow}>
        {['grid','overtime','pending','audit'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            style={activeTab === tab ? styles.tabActive : styles.tab}>
            {tab === 'grid' ? '📊 Grid View' : tab === 'overtime' ? '⏰ Overtime' : tab === 'pending' ? '⚠️ Pending' : '📝 Audit Log'}
          </button>
        ))}
      </div>

      {error && <div style={styles.errorMsg}>{error}</div>}

      {/* ── TAB: GRID VIEW ── */}
      {activeTab === 'grid' && (
        initialLoading ? <p>Loading attendance data...</p> : gridData ? (
          <div style={styles.gridWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, position: 'sticky', left: 0, zIndex: 2, minWidth: 200, background: '#f1f3f5' }}>Employee</th>
                  {Array.from({ length: gridData.days_in_month }, (_, i) => {
                    const dayDate = new Date(year, month - 1, i + 1);
                    const isWeekend = dayDate.getDay() === 0 || dayDate.getDay() === 6;
                    return (
                      <th key={i+1} style={{ ...styles.th, minWidth: 36, background: isWeekend ? '#e9ecef' : '#f1f3f5', fontSize: 11 }}>
                        <div>{['Su','Mo','Tu','We','Th','Fr','Sa'][dayDate.getDay()]}</div>
                        <div>{i+1}</div>
                      </th>
                    );
                  })}
                  <th style={{ ...styles.th, minWidth: 60 }}>Days</th>
                  <th style={{ ...styles.th, minWidth: 60 }}>Hours</th>
                  <th style={{ ...styles.th, minWidth: 50 }}>Late</th>
                  <th style={{ ...styles.th, minWidth: 50 }}>OT</th>
                  <th style={{ ...styles.th, minWidth: 70 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {gridData.grid.map((row, idx) => (
                  <tr key={row.employee.id}>
                    <td style={{ ...styles.td, position: 'sticky', left: 0, zIndex: 1, background: idx % 2 === 0 ? '#fff' : '#f8f9fa', fontWeight: 500 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input type="checkbox"
                          checked={selectedEmployees.includes(row.employee.id)}
                          onChange={(e) => {
                            if (e.target.checked) setSelectedEmployees(prev => [...prev, row.employee.id]);
                            else setSelectedEmployees(prev => prev.filter(x => x !== row.employee.id));
                          }}
                        />
                        <div>
                          <div style={{ fontSize: 13 }}>{row.employee.first_name} {row.employee.last_name}</div>
                          <div style={{ fontSize: 10, color: '#6c757d' }}>{row.employee.employee_code}</div>
                        </div>
                      </div>
                    </td>
                    {Array.from({ length: gridData.days_in_month }, (_, i) => {
                      const dayNum = i + 1;
                      const day = row.days?.[dayNum];
                      const bg = day?.is_weekend && !day?.status ? '#e9ecef' : getStatusColor(day);
                      return (
                        <td key={dayNum}
                          style={{ ...styles.td, background: bg, cursor: 'pointer', textAlign: 'center', padding: 2, fontSize: 11, position: 'relative' }}
                          onClick={() => openRecordModal(row, dayNum)}
                          title={day?.status || 'No record'}>
                          {day?.is_late && <span style={{ position: 'absolute', top: 0, right: 1, fontSize: 7, color: '#dc3545' }}>●</span>}
                          {day?.is_locked && <span style={{ position: 'absolute', top: 0, left: 1, fontSize: 7 }}>🔒</span>}
                          {getStatusLetter(day)}
                        </td>
                      );
                    })}
                    <td style={{ ...styles.td, textAlign: 'center', fontSize: 12 }}>{row.summary.worked_days}</td>
                    <td style={{ ...styles.td, textAlign: 'center', fontSize: 12 }}>{(row.summary.total_hours || 0).toFixed(1)}</td>
                    <td style={{ ...styles.td, textAlign: 'center', fontSize: 12, color: row.summary.late_count > 0 ? '#dc3545' : '#333' }}>
                      {row.summary.late_count}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'center', fontSize: 12, color: row.summary.overtime_hours > 0 ? '#0d6efd' : '#333' }}>
                      {(row.summary.overtime_hours || 0).toFixed(1)}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      <button style={styles.smallBtn} onClick={() => openSummary(row.employee.id)} title="Summary">📊</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p>No data available.</p>
      )}

      {/* ── TAB: OVERTIME ── */}
      {activeTab === 'overtime' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
            <h3 style={{ margin: 0 }}>Overtime Records</h3>
            <button style={styles.btnPrimary} onClick={() => { setOvertimeForm({ date: '', employee_id: '', hours: '', overtime_type: 'regular', notes: '' }); setShowOvertimeModal(true); }}>
              + Submit Overtime
            </button>
          </div>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Employee</th>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Hours</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {overtimeList.map(ot => (
                <tr key={ot.id}>
                  <td style={styles.td}>{ot.employee?.first_name} {ot.employee?.last_name}</td>
                  <td style={styles.td}>{ot.date}</td>
                  <td style={styles.td}>{ot.hours}h</td>
                  <td style={styles.td}><span style={styles.badge}>{ot.overtime_type}</span></td>
                  <td style={styles.td}>
                    <span style={{ ...styles.badge, background: ot.status === 'Approved' ? '#d4edda' : ot.status === 'Rejected' ? '#f8d7da' : '#fff3cd', color: '#333' }}>
                      {ot.status}
                    </span>
                  </td>
                  <td style={styles.td}>
                    {ot.status === 'Pending' && (
                      <>
                        <button style={{ ...styles.smallBtn, color: '#198754' }} onClick={() => handleOvertimeAction(ot.id, 'approve')}>✔</button>
                        <button style={{ ...styles.smallBtn, color: '#dc3545' }} onClick={() => handleOvertimeAction(ot.id, 'reject')}>✘</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {overtimeList.length === 0 && <tr><td style={styles.td} colSpan={6}>No overtime records for this period.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* ── TAB: PENDING ── */}
      {activeTab === 'pending' && (
        <div>
          <h3 style={{ marginBottom: 12 }}>Pending Actions ({pendingActions.length})</h3>
          {pendingActions.length === 0 ? <p style={{ color: '#6c757d' }}>No pending actions for this period. ✅</p> : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Employee</th>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Details</th>
                </tr>
              </thead>
              <tbody>
                {pendingActions.map((item, idx) => (
                  <tr key={idx}>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, background: item.type === 'missing_attendance' ? '#f8d7da' : item.type === 'pending_overtime' ? '#fff3cd' : '#cce5ff' }}>
                        {item.type.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td style={styles.td}>{item.employee?.first_name} {item.employee?.last_name}</td>
                    <td style={styles.td}>{item.date}</td>
                    <td style={styles.td}>{item.hours ? `${item.hours}h` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── TAB: AUDIT ── */}
      {activeTab === 'audit' && (
        <div>
          <h3 style={{ marginBottom: 12 }}>Audit Log</h3>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Date/Time</th>
                <th style={styles.th}>User</th>
                <th style={styles.th}>Employee</th>
                <th style={styles.th}>Action</th>
                <th style={styles.th}>Details</th>
              </tr>
            </thead>
            <tbody>
              {auditLogs.map(log => (
                <tr key={log.id}>
                  <td style={{ ...styles.td, fontSize: 12 }}>{new Date(log.created_at).toLocaleString()}</td>
                  <td style={styles.td}>{log.user?.name || '—'}</td>
                  <td style={styles.td}>{log.employee ? `${log.employee.first_name} ${log.employee.last_name}` : '—'}</td>
                  <td style={styles.td}><span style={styles.badge}>{log.action}</span></td>
                  <td style={{ ...styles.td, fontSize: 11, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {log.reason || log.new_value?.substring(0, 80) || '—'}
                  </td>
                </tr>
              ))}
              {auditLogs.length === 0 && <tr><td style={styles.td} colSpan={5}>No audit logs.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* ═══════════ MODALS ═══════════ */}

      {/* Record Modal */}
      {showRecordModal && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>
              {recordForm.existing_id ? 'Edit' : 'Add'} Attendance — {selectedEmployee?.first_name} {selectedEmployee?.last_name}
            </h3>
            <p style={{ fontSize: 13, color: '#6c757d', margin: '0 0 16px' }}>Date: {recordForm.date}</p>

            <div style={styles.formGroup}>
              <label style={styles.label}>Mode</label>
              <select value={recordForm.attendance_mode} onChange={e => setRecordForm(p => ({ ...p, attendance_mode: e.target.value }))} style={styles.input}>
                <option value="daily">Daily</option>
                <option value="hourly">Hourly</option>
              </select>
            </div>

            {gridData?.statuses && (
              <div style={styles.formGroup}>
                <label style={styles.label}>Status</label>
                <select value={recordForm.hr_attendance_status_id} onChange={e => setRecordForm(p => ({ ...p, hr_attendance_status_id: e.target.value }))} style={styles.input}>
                  <option value="">— Select Status —</option>
                  {gridData.statuses.map(s => (
                    <option key={s.id} value={s.id}>{s.code} — {s.name}</option>
                  ))}
                </select>
              </div>
            )}

            {recordForm.attendance_mode === 'hourly' && (
              <>
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ ...styles.formGroup, flex: 1 }}>
                    <label style={styles.label}>From</label>
                    <input type="time" value={recordForm.from_time} onChange={e => setRecordForm(p => ({ ...p, from_time: e.target.value }))} style={styles.input} />
                  </div>
                  <div style={{ ...styles.formGroup, flex: 1 }}>
                    <label style={styles.label}>To</label>
                    <input type="time" value={recordForm.to_time} onChange={e => setRecordForm(p => ({ ...p, to_time: e.target.value }))} style={styles.input} />
                  </div>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Break (minutes)</label>
                  <input type="number" value={recordForm.break_minutes} onChange={e => setRecordForm(p => ({ ...p, break_minutes: parseInt(e.target.value) || 0 }))} style={styles.input} />
                </div>
              </>
            )}

            <div style={styles.formGroup}>
              <label style={styles.label}>Notes</label>
              <textarea value={recordForm.notes} onChange={e => setRecordForm(p => ({ ...p, notes: e.target.value }))} style={{ ...styles.input, height: 60 }} />
            </div>

            <div style={styles.formGroup}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <input type="checkbox" checked={recordForm.transportation_eligible} onChange={e => setRecordForm(p => ({ ...p, transportation_eligible: e.target.checked }))} />
                Transportation Eligible
              </label>
            </div>

            <div style={styles.modalActions}>
              {recordForm.existing_id && (
                <button style={styles.btnDanger} onClick={deleteRecord}>Delete</button>
              )}
              <div style={{ flex: 1 }} />
              <button style={styles.btnSecondary} onClick={() => setShowRecordModal(false)}>Cancel</button>
              <button style={styles.btnPrimary} onClick={saveRecord}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Overtime Submit Modal */}
      {showOvertimeModal && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Submit Overtime</h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>Employee</label>
              <select value={overtimeForm.employee_id || ''} onChange={e => setOvertimeForm(p => ({ ...p, employee_id: e.target.value }))} style={styles.input}>
                <option value="">— Select —</option>
                {gridData?.grid?.map(row => (
                  <option key={row.employee.id} value={row.employee.id}>{row.employee.first_name} {row.employee.last_name}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ ...styles.formGroup, flex: 1 }}>
                <label style={styles.label}>Date</label>
                <input type="date" value={overtimeForm.date || ''} onChange={e => setOvertimeForm(p => ({ ...p, date: e.target.value }))} style={styles.input} />
              </div>
              <div style={{ ...styles.formGroup, flex: 1 }}>
                <label style={styles.label}>Hours</label>
                <input type="number" step="0.25" min="0.25" value={overtimeForm.hours || ''} onChange={e => setOvertimeForm(p => ({ ...p, hours: parseFloat(e.target.value) }))} style={styles.input} />
              </div>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Type</label>
              <select value={overtimeForm.overtime_type || 'regular'} onChange={e => setOvertimeForm(p => ({ ...p, overtime_type: e.target.value }))} style={styles.input}>
                <option value="regular">Regular</option>
                <option value="weekend">Weekend</option>
                <option value="holiday">Holiday</option>
                <option value="night">Night</option>
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Notes</label>
              <textarea value={overtimeForm.notes || ''} onChange={e => setOvertimeForm(p => ({ ...p, notes: e.target.value }))} style={{ ...styles.input, height: 60 }} />
            </div>
            <div style={styles.modalActions}>
              <button style={styles.btnSecondary} onClick={() => setShowOvertimeModal(false)}>Cancel</button>
              <button style={styles.btnPrimary} onClick={submitOvertime}>Submit</button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Action Modal */}
      {showBulkModal && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Bulk Action</h3>
            <p style={{ fontSize: 13, color: '#6c757d' }}>
              {selectedEmployees.length} employee(s) selected. Select more from the grid checkboxes.
            </p>
            <div style={styles.formGroup}>
              <label style={styles.label}>Action</label>
              <select value={bulkAction} onChange={e => setBulkAction(e.target.value)} style={styles.input}>
                <option value="set_status">Set Status</option>
                <option value="mark_missing">Mark as Missing</option>
              </select>
            </div>
            {bulkAction === 'set_status' && gridData?.statuses && (
              <div style={styles.formGroup}>
                <label style={styles.label}>Status</label>
                <select value={bulkStatusId} onChange={e => setBulkStatusId(e.target.value)} style={styles.input}>
                  <option value="">— Select —</option>
                  {gridData.statuses.map(s => <option key={s.id} value={s.id}>{s.code} — {s.name}</option>)}
                </select>
              </div>
            )}
            <div style={styles.formGroup}>
              <label style={styles.label}>Dates (comma-separated: YYYY-MM-DD, YYYY-MM-DD)</label>
              <input type="text" value={bulkDates} onChange={e => setBulkDates(e.target.value)} style={styles.input} placeholder="2026-08-01, 2026-08-02" />
            </div>
            <div style={styles.modalActions}>
              <button style={styles.btnSecondary} onClick={() => setShowBulkModal(false)}>Cancel</button>
              <button style={styles.btnPrimary} onClick={submitBulk}>Apply</button>
            </div>
          </div>
        </div>
      )}

      {/* Summary Modal */}
      {showSummaryModal && summaryData && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Monthly Summary — {monthNames[summaryData.month - 1]} {summaryData.year}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 16 }}>
              {[
                ['Worked Days', summaryData.worked_days],
                ['On Site', summaryData.on_site],
                ['Remote', summaryData.remote],
                ['Leave', summaryData.leave],
                ['Missing', summaryData.missing_attendance],
                ['Late Count', summaryData.late_count],
                ['Total Hours', `${(summaryData.total_hours || 0).toFixed(1)}h`],
                ['Regular Hours', `${(summaryData.regular_hours || 0).toFixed(1)}h`],
                ['Overtime (Approved)', `${(summaryData.overtime_approved || 0).toFixed(1)}h`],
                ['Overtime (Pending)', `${(summaryData.overtime_pending || 0).toFixed(1)}h`],
                ['Missing Hours', `${(summaryData.missing_hours || 0).toFixed(1)}h`],
                ['Transportation Units', summaryData.transportation_units],
              ].map(([label, val]) => (
                <div key={label} style={{ padding: 8, background: '#f8f9fa', borderRadius: 6 }}>
                  <div style={{ fontSize: 11, color: '#6c757d' }}>{label}</div>
                  <div style={{ fontSize: 18, fontWeight: 600 }}>{val}</div>
                </div>
              ))}
            </div>
            <div style={styles.modalActions}>
              <button style={styles.btnSecondary} onClick={() => setShowSummaryModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════
//  STYLES
// ═══════════════════════════════════════

const styles = {
  container: { padding: 24, maxWidth: '100%', fontFamily: "'Segoe UI', sans-serif" },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 },
  title: { margin: 0, fontSize: 24, fontWeight: 700 },
  headerActions: { display: 'flex', gap: 8 },
  lockBanner: { background: '#fff3cd', color: '#856404', padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 14, border: '1px solid #ffc107' },
  monthNav: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16, justifyContent: 'center' },
  navBtn: { background: '#e9ecef', border: 'none', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 16, fontWeight: 600 },
  monthLabel: { fontSize: 20, fontWeight: 600, minWidth: 160, textAlign: 'center' },
  summaryRow: { display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' },
  summaryCard: { flex: '1 1 140px', background: '#fff', borderRadius: 8, padding: '12px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderLeft: '4px solid #0d6efd' },
  summaryNum: { fontSize: 22, fontWeight: 700 },
  summaryLabel: { fontSize: 12, color: '#6c757d', marginTop: 2 },
  filterRow: { display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' },
  searchInput: { flex: '1 1 200px', padding: '8px 12px', borderRadius: 6, border: '1px solid #ced4da', fontSize: 14, outline: 'none' },
  filterSelect: { padding: '8px 12px', borderRadius: 6, border: '1px solid #ced4da', fontSize: 14, outline: 'none', minWidth: 150 },
  tabRow: { display: 'flex', gap: 4, marginBottom: 16, borderBottom: '2px solid #e9ecef', paddingBottom: 0 },
  tab: { background: 'none', border: 'none', padding: '8px 16px', cursor: 'pointer', fontSize: 14, color: '#6c757d', borderBottom: '2px solid transparent', marginBottom: -2 },
  tabActive: { background: 'none', border: 'none', padding: '8px 16px', cursor: 'pointer', fontSize: 14, color: '#0d6efd', fontWeight: 600, borderBottom: '2px solid #0d6efd', marginBottom: -2 },
  errorMsg: { background: '#f8d7da', color: '#842029', padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 14 },
  gridWrapper: { overflowX: 'auto', borderRadius: 8, border: '1px solid #dee2e6' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { background: '#f1f3f5', padding: '8px 6px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' },
  td: { padding: '6px', borderBottom: '1px solid #eee', whiteSpace: 'nowrap' },
  smallBtn: { background: 'none', border: '1px solid #dee2e6', borderRadius: 4, padding: '2px 6px', cursor: 'pointer', fontSize: 14 },
  badge: { padding: '2px 8px', borderRadius: 10, fontSize: 11, background: '#e9ecef', display: 'inline-block' },
  btnPrimary: { background: '#0d6efd', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', cursor: 'pointer', fontSize: 14, fontWeight: 500 },
  btnSecondary: { background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', cursor: 'pointer', fontSize: 14, fontWeight: 500 },
  btnWarning: { background: '#ffc107', color: '#212529', border: 'none', borderRadius: 6, padding: '8px 16px', cursor: 'pointer', fontSize: 14, fontWeight: 500 },
  btnDanger: { background: '#dc3545', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', cursor: 'pointer', fontSize: 14, fontWeight: 500 },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', borderRadius: 12, padding: 24, width: '90%', maxWidth: 520, maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' },
  modalTitle: { margin: '0 0 16px', fontSize: 18, fontWeight: 600 },
  modalActions: { display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' },
  formGroup: { marginBottom: 12 },
  label: { display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4, color: '#333' },
  input: { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ced4da', fontSize: 14, outline: 'none', boxSizing: 'border-box' },
};